'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { GameData } from '@/features/games/shared/types/game-data';
import { WebGameFrame } from '@/features/games/internal/components/game-detail/web-game/web-game-frame';
import { cn } from '@/shared/utils/cn';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useTranslations } from 'next-intl';
import { CtaLink } from '@/shared/components/link/cta-link';
import { Maximize2, X } from 'lucide-react';
import { emitGTMEvent } from '@/features/gtm/shared/helper/emit-gtm-event';
import { Logger } from '@/shared/helpers/logger/logger';

type WebGamePlayerProps = {
  game: GameData;
  src: string;
  className?: string;
};

type FullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

// ID was used for showing the precontent ad, but is currently not needed. Keeping it for potential future use.
const WEB_GAME_CONTAINER_ID = 'wt-web-game-container';

const getFullscreenElement = (doc: FullscreenDocument) =>
  doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;

export const WebGamePlayer = ({ game, src, className }: WebGamePlayerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { authenticatedUser } = useUser();
  const t = useTranslations('GamePage');
  const isWildMember = Boolean(authenticatedUser?.isWildMember);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showRestrictionMessage, setShowRestrictionMessage] = useState(false);
  const [showFullscreenControl, setShowFullscreenControl] = useState(false);
  const hideControlsTimeoutRef = useRef<number | null>(null);
  const hideRestrictionTimeoutRef = useRef<number | null>(null);

  const handleFullscreenChange = useCallback(() => {
    if (typeof document === 'undefined') return;
    const doc = document as FullscreenDocument;
    const currentFullScreenElement = getFullscreenElement(doc);
    setIsFullscreen(currentFullScreenElement === containerRef.current);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handler = () => handleFullscreenChange();
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);

    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, [handleFullscreenChange]);

  const requestFullscreen = useCallback(async () => {
    const el = containerRef.current as (FullscreenElement & Element) | null;
    if (!el) return;
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen({ navigationUI: 'hide' });
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } catch (error) {
      Logger.error('Failed to enter fullscreen for web game', {
        gamePath: game.path,
        error,
      });
    }
  }, [game.path]);

  const exitFullscreen = useCallback(async () => {
    const doc = document as FullscreenDocument;
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
    } catch (error) {
      Logger.error('Failed to exit fullscreen for web game', {
        gamePath: game.path,
        error,
      });
    }
  }, [game.path]);

  const toggleFullscreen = useCallback(async () => {
    if (!isWildMember) {
      emitGTMEvent('FULLSCREEN_ICON_CLICK');
      setShowRestrictionMessage(true);
      return;
    }
    if (isFullscreen) {
      await exitFullscreen();
    } else {
      emitGTMEvent('FULLSCREEN_ICON_CLICK');
      await requestFullscreen();
    }
  }, [exitFullscreen, isFullscreen, isWildMember, requestFullscreen]);

  const clearHideControlsTimeout = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
  }, []);

  const clearHideRestrictionTimeout = useCallback(() => {
    if (hideRestrictionTimeoutRef.current) {
      clearTimeout(hideRestrictionTimeoutRef.current);
      hideRestrictionTimeoutRef.current = null;
    }
  }, []);

  const handleRestrictionVisibility = useCallback(
    (visible: boolean) => {
      if (isWildMember) return;
      clearHideRestrictionTimeout();
      if (visible) {
        setShowRestrictionMessage(true);
        return;
      }
      hideRestrictionTimeoutRef.current = window.setTimeout(() => {
        setShowRestrictionMessage(false);
        hideRestrictionTimeoutRef.current = null;
      }, 250);
    },
    [clearHideRestrictionTimeout, isWildMember],
  );

  const scheduleHideControls = useCallback(() => {
    if (!isFullscreen) return;
    clearHideControlsTimeout();
    hideControlsTimeoutRef.current = window.setTimeout(() => {
      setShowFullscreenControl(false);
    }, 1500);
  }, [clearHideControlsTimeout, isFullscreen]);

  const revealControls = useCallback(() => {
    if (!isFullscreen) return;
    setShowFullscreenControl(true);
    scheduleHideControls();
  }, [isFullscreen, scheduleHideControls]);

  useEffect(() => {
    return () => clearHideControlsTimeout();
  }, [clearHideControlsTimeout]);

  useEffect(() => {
    return () => clearHideRestrictionTimeout();
  }, [clearHideRestrictionTimeout]);

  useEffect(() => {
    if (!isFullscreen) {
      clearHideControlsTimeout();
      return;
    }
    const revealFrameId = window.requestAnimationFrame(() => {
      revealControls();
    });
    if (typeof document === 'undefined') return;
    const handleMouseMove = (event: MouseEvent) => {
      if (event.clientY <= 80) {
        revealControls();
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.cancelAnimationFrame(revealFrameId);
      document.removeEventListener('mousemove', handleMouseMove);
      clearHideControlsTimeout();
    };
  }, [clearHideControlsTimeout, isFullscreen, revealControls]);

  const buttonAriaLabel = isFullscreen ? t('exitFullscreen') : t('enterFullscreen');
  const isRestrictionVisible = !isWildMember && showRestrictionMessage;
  const isFullscreenControlVisible = isFullscreen && showFullscreenControl;
  const buttonIcon = isFullscreen ? (
    <X className='h-5 w-5' aria-hidden='true' />
  ) : (
    <Maximize2 className='h-5 w-5' aria-hidden='true' />
  );

  return (
    <div className={cn('flex w-full flex-col', className)}>
      {!isFullscreen && (
        <div className='mb-2 flex justify-end'>
          <div
            className='relative flex flex-col items-end'
            onMouseEnter={() => handleRestrictionVisibility(true)}
            onMouseLeave={() => handleRestrictionVisibility(false)}
            onFocusCapture={() => handleRestrictionVisibility(true)}
            onBlurCapture={() => handleRestrictionVisibility(false)}
          >
            <button
              type='button'
              onClick={toggleFullscreen}
              className={cn(
                'bg-background-panel/80 hover:bg-background-panel lg:hover:text-text-hover flex h-11 w-11 items-center justify-center rounded-sm border border-white text-white transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                !isWildMember && 'hover:bg-background-panel',
              )}
              aria-label={buttonAriaLabel}
              aria-pressed={isFullscreen}
              aria-disabled={!isWildMember}
              data-gtm='cta'
              data-cta-name='open_fullscreen'
            >
              {buttonIcon}
            </button>
            {isRestrictionVisible && (
              <div
                role='tooltip'
                className='bg-background-panel ring-border absolute top-full right-0 z-30 mt-2 w-64 rounded-md p-4 text-left text-sm shadow-lg ring-1'
              >
                <p className='font-body-bold text-text text-base'>{t('fullscreenUpsellTitle')}</p>
                <CtaLink
                  href='/join-wild'
                  className='mt-3 w-full justify-center'
                  data-gtm='cta'
                  data-cta-name='navigate_to_join_wild'
                >
                  {t('fullscreenUpsellCta')}
                </CtaLink>
              </div>
            )}
          </div>
        </div>
      )}
      <div
        id={WEB_GAME_CONTAINER_ID}
        ref={containerRef}
        className={cn(
          'web-game-fullscreen relative flex-1 overflow-hidden rounded-md bg-black',
          isFullscreen ? 'w-full' : '',
        )}
      >
        {isFullscreen && (
          <button
            type='button'
            onClick={toggleFullscreen}
            className={cn(
              'bg-background-panel/80 hover:bg-background-panel lg:hover:text-text-hover absolute top-3 right-3 z-20 flex h-10 w-10 items-center justify-center rounded-sm border border-white text-white transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
              isFullscreenControlVisible
                ? 'pointer-events-auto opacity-100'
                : 'pointer-events-none opacity-0',
            )}
            aria-label={buttonAriaLabel}
            aria-pressed={isFullscreen}
          >
            {buttonIcon}
          </button>
        )}
        <WebGameFrame game={game} src={src} />
      </div>
    </div>
  );
};
